import type { ReactNode } from "react";

/* -------------------------------------------------------------------------- */
/*  Brand logos                                                               */
/*                                                                            */
/*  Most brands use a static file from public/product-icon via <img>. The two */
/*  gradient logos with no local file (Master Addons, Master Blocks) live in   */
/*  brandLogos.tsx (client, useId'd ids). This module stays server-safe.       */
/* -------------------------------------------------------------------------- */

const svg = "jlt-block jlt-h-[72%] jlt-w-[72%] jlt-object-contain";

/**
 * Static logo from public/product-icon (mixed svg/png/webp). Plain <img> so
 * next/image's remote-SVG restriction never applies and nothing is optimized —
 * these are tiny static icons.
 */
const ProductIcon = ({ src, alt }: { src: string; alt: string }) => (
  // eslint-disable-next-line @next/next/no-img-element -- local static mixed formats, no optimizer
  <img src={src} alt={alt} className={svg} loading="lazy" decoding="async" />
);

/* -------------------------------------------------------------------------- */
/*  Content                                                                   */
/* -------------------------------------------------------------------------- */

export type Brand = {
  name: string;
  href: string;
  /** set "_blank" for external brand sites — consumers add the safe rel */
  target?: "_blank";
  category: string;
  /** short label used in the header mega menu */
  tagline: string;
  description: string;
  meta: string;
  pill?: "hot" | "new";
  blob: string;
  logo: ReactNode;
};

export const brands: Brand[] = [
  {
    name: "WP Adminify",
    href: "https://wpadminify.com",
    target: "_blank",
    category: "Dashboard",
    tagline: "Dashboard customization",
    description: "Folders, dark mode, menu editor, white-label 20+ modules for wp-admin.",
    meta: "7,000+ installs",
    pill: "hot",
    blob: "jlt-bg-pl-lilac",
    logo: <ProductIcon src="/product-icon/wp-adminify.png" alt="WP Adminify" />,
  },
  {
    name: "WP Spotlight",
    href: "https://wpspotlight.pro",
    target: "_blank",
    category: "Dashboard",
    tagline: "Lightning-fast site search",
    description:
      "Search your whole site in a keystroke - posts, media, plugins, WooCommerce orders.",
    meta: "10+ installs",
    pill: "new",
    blob: "jlt-bg-pl-peach",
    logo: <ProductIcon src="/product-icon/wp-spotlight.svg" alt="WP Spotlight" />,
  },
  {
    name: "Admin Bar Editor",
    href: "https://jeweltheme.com/admin-bar-editor",
    target: "_blank",
    category: "Dashboard",
    tagline: "Admin bar control",
    description: "Shape the admin bar for every role - hide, rename, reorder, brand.",
    meta: "3,000+ installs",
    blob: "jlt-bg-pl-peach",
    logo: <ProductIcon src="/product-icon/admin-bar.webp" alt="Admin Bar Editor" />,
  },
  {
    name: "Loginfy",
    href: "https://wpadminify.com/loginfy",
    target: "_blank",
    category: "Dashboard",
    tagline: "Login security & design",
    description: "Login pages clients love secure, branded, template-ready.",
    meta: "2,000+ installs",
    blob: "jlt-bg-pl-sky",
    logo: <ProductIcon src="/product-icon/loginfy.svg" alt="Loginfy" />,
  },
  {
    name: "Master Addons",
    href: "https://master-addons.com",
    target: "_blank",
    category: "Page Builder",
    tagline: "60+ Elementor addons",
    description: "60+ advanced Elementor addons that make pro design feel effortless.",
    meta: "40,000+ installs",
    pill: "hot",
    blob: "jlt-bg-pl-lilac",
    logo: <ProductIcon src="/product-icon/master-addons.webp" alt="Master Addons" />,
  },
  {
    name: "Master Blocks",
    href: "https://jeweltheme.com/master-blocks",
    target: "_blank",
    category: "Page Builder",
    tagline: "Gutenberg block library",
    description: "Powerful Gutenberg blocks for stunning pages in the native editor.",
    meta: "100+ installs",
    blob: "jlt-bg-pl-mint",
    logo: <ProductIcon src="/product-icon/master-blocks.webp" alt="Master Blocks" />,
  },
  {
    name: "Jewel Theme",
    href: "https://jeweltheme.com",
    target: "_blank",
    category: "Themes",
    tagline: "WordPress themes",
    description: "Lightweight WordPress themes crafted for speed and beautiful typography.",
    meta: "15,000+ sites",
    blob: "jlt-bg-pl-peach",
    logo: <ProductIcon src="/product-icon/jeweltheme.svg" alt="Jewel Theme" />,
  },
  {
    name: "React Admin",
    href: "https://reactadmin.com",
    target: "_blank",
    category: "Developer",
    tagline: "React dashboard templates",
    description: "Free & pro React dashboard templates for shipping internal tools fast.",
    meta: "25,000+ downloads",
    pill: "new",
    blob: "jlt-bg-pl-mint",
    logo: <ProductIcon src="/product-icon/reactadmin.svg" alt="React Admin" />,
  },
];
